import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  Check, Circle, Loader2, ArrowRight, Youtube, Crosshair, Tag,
  Sparkles, X, ExternalLink, Copy,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** Hook that polls /api/onboarding/status. Returns {status, refresh}. */
export function useOnboarding(enabled = true) {
  const [status, setStatus] = useState(null);
  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await axios.get(`${API}/onboarding/status`);
      setStatus(data);
    } catch (e) { console.error(e); }
  }, [enabled]);
  useEffect(() => { refresh(); }, [refresh]);
  return { status, refresh };
}

/** Three-step wizard shown as a modal overlay until the user completes (or dismisses) it. */
export function OnboardingWizard({ status, onRefresh, onClose, onUpgrade, billing }) {
  const [activeStep, setActiveStep] = useState(0);

  // Auto-advance to the first incomplete step
  useEffect(() => {
    if (!status) return;
    if (!status.youtube_oauth_connected) setActiveStep(0);
    else if (!status.has_offer) setActiveStep(1);
    else if (!status.pixel_installed) setActiveStep(2);
    else setActiveStep(2);
  }, [status]);

  if (!status) return null;

  const steps = [
    {
      id: "youtube",
      label: "Connect YouTube",
      icon: Youtube,
      done: status.youtube_oauth_connected,
      blurb: "Authorize VidROI to read your channel's videos using your own YouTube quota.",
    },
    {
      id: "offer",
      label: "Add an offer",
      icon: Tag,
      done: status.has_offer,
      blurb: "Tell us the landing page you promote on YouTube — we'll mint short links to it.",
    },
    {
      id: "pixel",
      label: "Install the pixel",
      icon: Crosshair,
      done: status.pixel_installed,
      blurb: "Paste a one-line snippet on your site. We'll verify automatically once we see a click.",
    },
  ];

  const allDone = steps.every((s) => s.done);

  const dismiss = async () => {
    // Optimistic: hide immediately so the resume-banner doesn't flash for ~1s
    // while we wait for /onboarding/status to return completed=true.
    onClose?.();
    try {
      await axios.post(`${API}/onboarding/complete`);
      onRefresh?.();
    } catch (err) {
      console.warn("onboarding/complete failed (banner may re-appear):", err?.message || err);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 noise"
      data-testid="onboarding-wizard"
    >
      <div className="relative w-full max-w-3xl bg-zinc-950 border border-white/10 rounded-2xl shadow-[0_30px_120px_-30px_rgba(255,0,0,0.45)] overflow-hidden animate-fade-up">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-red-500/15 blur-[120px] pointer-events-none" />

        {/* Top bar */}
        <div className="relative flex items-start justify-between px-7 sm:px-9 pt-7">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-[10px] uppercase tracking-[0.2em] font-semibold">
              <Sparkles className="w-3 h-3" />
              Get set up · 3 steps
            </div>
            <h2 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight mt-3">
              Let's wire VidROI to your channel.
            </h2>
            <p className="text-sm text-zinc-400 mt-1">A 3-minute setup — then you'll see revenue per video.</p>
          </div>
          <button
            onClick={dismiss}
            className="text-zinc-500 hover:text-white p-1 -mr-2 -mt-1"
            data-testid="onboarding-close"
            title="Skip for now"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step rail */}
        <div className="relative px-7 sm:px-9 pt-7 pb-2">
          <div className="flex items-center gap-2 sm:gap-3" data-testid="onboarding-step-rail">
            {steps.map((s, i) => {
              const active = i === activeStep;
              return (
                <React.Fragment key={s.id}>
                  <button
                    type="button"
                    onClick={() => setActiveStep(i)}
                    className={`group flex items-center gap-2 px-3 py-2 rounded-lg transition-all
                      ${active ? "bg-white/[0.04] border border-white/15" : "border border-transparent"}`}
                    data-testid={`onboarding-tab-${s.id}`}
                  >
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold
                      ${s.done ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                              : active ? "bg-red-500/15 text-red-300 border border-red-500/30"
                                       : "bg-white/[0.04] text-zinc-500 border border-white/10"}`}>
                      {s.done ? <Check className="w-3.5 h-3.5" /> : i + 1}
                    </span>
                    <span className={`text-sm ${active ? "text-white" : s.done ? "text-zinc-300" : "text-zinc-500"}`}>
                      {s.label}
                    </span>
                  </button>
                  {i < steps.length - 1 && <div className="flex-1 h-px bg-white/[0.06]" />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step body */}
        <div className="relative px-7 sm:px-9 pb-7 pt-2 min-h-[300px]">
          {activeStep === 0 && (
            <StepYouTube
              status={status}
              onConnected={onRefresh}
              billing={billing}
              onUpgrade={onUpgrade}
              onNext={() => setActiveStep(1)}
            />
          )}
          {activeStep === 1 && (
            <StepOffer
              status={status}
              onCreated={onRefresh}
              billing={billing}
              onUpgrade={onUpgrade}
              onNext={() => setActiveStep(2)}
            />
          )}
          {activeStep === 2 && (
            <StepPixel
              status={status}
              onRefresh={onRefresh}
              billing={billing}
              onUpgrade={onUpgrade}
              onDone={dismiss}
              allDone={allDone}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — Connect YouTube via OAuth (opens Google consent in a popup window)
// ---------------------------------------------------------------------------
function StepYouTube({ status, onConnected, billing, onUpgrade, onNext }) {
  const [busy, setBusy] = useState(false);
  const popupRef = useRef(null);
  const intervalRef = useRef(null);

  // Listen for the postMessage from /api/youtube/oauth/callback
  useEffect(() => {
    const handler = (e) => {
      if (!e.data || e.data.type !== "vidroi-yt-oauth") return;
      if (e.data.success) {
        toast.success("YouTube connected");
        onConnected?.();
      } else {
        toast.error(e.data.message || "YouTube authorization canceled");
      }
      setBusy(false);
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (popupRef.current && !popupRef.current.closed) {
        try { popupRef.current.close(); }
        catch (err) { /* popup may already be on a cross-origin page (Google) — close() may throw; harmless */
          console.debug("popup close swallowed:", err?.message); }
      }
    };
    window.addEventListener("message", handler);
    return () => {
      window.removeEventListener("message", handler);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [onConnected]);

  const connect = async () => {
    if (billing?.plan === "free") {
      onUpgrade?.();
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/youtube/oauth/start`);
      popupRef.current = window.open(data.auth_url, "vidroi-yt-oauth", "width=500,height=720");
      // Poll for popup close to clear the busy state if the user just X-d out
      intervalRef.current = setInterval(() => {
        if (popupRef.current && popupRef.current.closed) {
          setBusy(false);
          if (intervalRef.current) clearInterval(intervalRef.current);
          // Refresh anyway in case the callback succeeded but postMessage was blocked
          onConnected?.();
        }
      }, 800);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "YouTube OAuth isn't configured yet — drop your Google Client ID + Secret in backend/.env.");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect YouTube? VidROI will fall back to the shared API quota.")) return;
    try {
      await axios.delete(`${API}/youtube/oauth/disconnect`);
      toast.success("YouTube disconnected");
      onConnected?.();
    } catch {
      toast.error("Couldn't disconnect");
    }
  };

  // Disconnect + immediately re-open the Google account chooser so the user can
  // pick a different YouTube channel in one click instead of two.
  const switchAccount = async () => {
    if (billing?.plan === "free") {
      onUpgrade?.();
      return;
    }
    setBusy(true);
    try {
      await axios.delete(`${API}/youtube/oauth/disconnect`);
      // Refresh parent state so the "Connect" UI re-renders if the user closes the popup.
      onConnected?.();
      // Backend already sends prompt=consent select_account on /start, so the
      // chooser will appear even if the user is signed into Google.
      const { data } = await axios.get(`${API}/youtube/oauth/start`);
      popupRef.current = window.open(data.auth_url, "vidroi-yt-oauth", "width=500,height=720");
      intervalRef.current = setInterval(() => {
        if (popupRef.current && popupRef.current.closed) {
          setBusy(false);
          if (intervalRef.current) clearInterval(intervalRef.current);
          onConnected?.();
        }
      }, 800);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't switch account — try Disconnect then Connect.");
      setBusy(false);
    }
  };

  if (status.youtube_oauth_connected) {
    const channelTitle = status.youtube_channel_title;
    const channelThumb = status.youtube_channel_thumbnail;
    const subs = status.youtube_subscriber_count;
    const subsLabel = typeof subs === "number" && subs > 0
      ? (subs >= 1000 ? `${(subs / 1000).toFixed(subs >= 10000 ? 0 : 1)}k subs` : `${subs} subs`)
      : null;
    return (
      <div data-testid="onboarding-step-youtube">
        <StepHeader title="YouTube channel connected" subtitle="VidROI is using your own quota for channel + video reads." />
        <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.05] p-4 flex items-center gap-3 mb-5">
          {channelThumb ? (
            <img
              src={channelThumb}
              alt={channelTitle || "YouTube channel"}
              className="w-10 h-10 rounded-xl object-cover border border-emerald-500/30"
              data-testid="onboarding-yt-channel-thumb"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 flex items-center justify-center"><Check className="w-5 h-5" /></div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-white font-medium truncate" data-testid="onboarding-yt-channel-name">
              {channelTitle ? `Connected as ${channelTitle}` : "Connected"}
            </div>
            <div className="text-xs text-zinc-400 truncate">
              {subsLabel ? `${subsLabel} · ` : ""}Your own 10k/day quota is now in use.
            </div>
          </div>
          <Button
            onClick={switchAccount}
            disabled={busy}
            variant="ghost"
            className="text-zinc-400 hover:text-white"
            data-testid="onboarding-yt-switch-account"
          >
            {busy ? "Switching…" : "Switch account"}
          </Button>
          <Button onClick={disconnect} variant="ghost" className="text-zinc-500 hover:text-red-400" data-testid="onboarding-yt-disconnect">Disconnect</Button>
        </div>
        <Button onClick={onNext} className="bg-red-600 hover:bg-red-500 text-white border border-red-400/30 shadow-[0_0_18px_rgba(255,0,0,0.35)]" data-testid="onboarding-step1-next">
          Next: add an offer <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="onboarding-step-youtube">
      <StepHeader title="Connect your YouTube channel" subtitle="VidROI will use YOUR OAuth token for every YouTube API read — so quota costs come from your own 10k/day budget, not a shared one." />
      <ul className="space-y-2 mb-6 text-sm text-zinc-300">
        <Bullet>Reads view counts, channel info, and your videos list.</Bullet>
        <Bullet>Can update a video description <em>only</em> when you click "Inject link" on that row — never automatically.</Bullet>
        <Bullet>You can revoke any time from this dialog or your Google account settings.</Bullet>
      </ul>
      <Button
        onClick={connect}
        disabled={busy}
        className="bg-white text-zinc-900 hover:bg-zinc-100 font-medium h-11 px-5"
        data-testid="onboarding-yt-connect"
      >
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Waiting for Google…</> : <><GoogleMark /><span className="ml-2.5">Connect with Google</span></>}
      </Button>
      {busy && (
        <p className="text-xs text-zinc-500 mt-3">A popup opened — complete the Google flow there. We'll pick it up automatically.</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — Add an offer (inline mini-form; defers to /api/offers under the hood)
// ---------------------------------------------------------------------------
function StepOffer({ status, onCreated, billing, onUpgrade, onNext }) {
  const [name, setName] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim() || !baseUrl.trim()) { toast.error("Name + URL are required"); return; }
    if (billing?.plan === "free") { onUpgrade?.(); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/offers`, { name, base_url: baseUrl });
      toast.success("Offer added");
      onCreated?.();
      onNext();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message;
      toast.error(msg || "Couldn't save offer");
    } finally {
      setSaving(false);
    }
  };

  if (status.has_offer) {
    return (
      <div data-testid="onboarding-step-offer">
        <StepHeader title="Offer ready" subtitle="Your videos can now mint short links pointing here." />
        <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.05] p-4 flex items-center gap-3 mb-5">
          <Check className="w-5 h-5 text-emerald-400" />
          <div className="text-zinc-200 text-sm">At least one offer configured — you're set.</div>
        </div>
        <Button onClick={onNext} className="bg-red-600 hover:bg-red-500 text-white border border-red-400/30" data-testid="onboarding-step2-next">
          Next: install the pixel <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="onboarding-step-offer">
      <StepHeader title="Add the page you're promoting" subtitle="This is the landing page or product URL your YouTube viewers will click to. We'll generate short links + UTMs for it automatically." />
      <div className="space-y-3 mb-5">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-1.5">Offer name</div>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Course launch"
            className="bg-black/40 border-white/10 text-white"
            data-testid="onboarding-offer-name"
          />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-1.5">Landing page URL</div>
          <Input
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="https://launch.example.com"
            className="bg-black/40 border-white/10 text-white font-mono text-sm"
            data-testid="onboarding-offer-url"
          />
        </div>
      </div>
      <Button
        onClick={save}
        disabled={saving}
        className="bg-red-600 hover:bg-red-500 text-white border border-red-400/30 shadow-[0_0_18px_rgba(255,0,0,0.35)]"
        data-testid="onboarding-offer-save"
      >
        {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Check className="w-4 h-4 mr-1.5" /> Save offer</>}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — Install the pixel + verify
// ---------------------------------------------------------------------------
function StepPixel({ status, onRefresh, onDone, allDone }) {
  const [snippet, setSnippet] = useState("");
  const [copied, setCopied] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/tracking/pixel`);
        setSnippet(data.pixel_code || "");
      } catch (err) {
        console.warn("Couldn't load pixel snippet:", err?.message || err);
      }
    })();
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.success("Snippet copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy — please select and copy manually.");
    }
  };

  const verify = async () => {
    setChecking(true);
    try {
      await onRefresh?.();
    } finally {
      setChecking(false);
    }
  };

  return (
    <div data-testid="onboarding-step-pixel">
      <StepHeader title="Install the tracking pixel" subtitle="Paste this snippet on every page of your site (or just the thank-you page). We'll start counting clicks + sales automatically." />

      {status.pixel_installed ? (
        <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.05] p-4 flex items-center gap-3 mb-5" data-testid="onboarding-pixel-verified">
          <Check className="w-5 h-5 text-emerald-400" />
          <div className="text-zinc-200 text-sm">Pixel verified — we've seen at least one click. You're set.</div>
        </div>
      ) : (
        <div className="rounded-xl border border-white/10 bg-black/40 mb-5 overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 bg-white/[0.02]">
            <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Universal pixel snippet</span>
            <Button size="sm" variant="ghost" onClick={copy} className="text-zinc-300 hover:text-white text-xs" data-testid="onboarding-pixel-copy">
              {copied ? <><Check className="w-3.5 h-3.5 mr-1" /> Copied</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Copy</>}
            </Button>
          </div>
          <pre className="text-[11px] font-mono text-zinc-300 p-3 max-h-44 overflow-y-auto whitespace-pre-wrap break-all">{snippet || "Loading…"}</pre>
        </div>
      )}

      <div className="flex gap-2">
        {!status.pixel_installed && (
          <Button
            onClick={verify}
            disabled={checking}
            variant="outline"
            className="bg-white/[0.04] border-white/15 text-zinc-200 hover:bg-white/[0.08] hover:text-white"
            data-testid="onboarding-pixel-verify"
          >
            {checking ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking…</> : "Verify installation"}
          </Button>
        )}
        <Button
          onClick={onDone}
          className="bg-red-600 hover:bg-red-500 text-white border border-red-400/30 shadow-[0_0_18px_rgba(255,0,0,0.35)]"
          data-testid="onboarding-finish"
        >
          {allDone ? <><Check className="w-4 h-4 mr-1.5" /> All set — open dashboard</> : "Finish later"}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mini helpers
// ---------------------------------------------------------------------------
function StepHeader({ title, subtitle }) {
  return (
    <div className="mb-6">
      <h3 className="text-xl font-semibold text-white tracking-tight">{title}</h3>
      <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{subtitle}</p>
    </div>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2.5">
      <Check className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
      <span>{children}</span>
    </li>
  );
}

function GoogleMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}
